import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '../../lib/apiClient';
import { usePortalPatientData } from './portalContext';
import { ErrorBanner, PortalPage, TableCard, formatPortalCurrency, formatPortalDate, statusPillClass } from './ui';

type PortalInvoice = {
  id: string;
  invoiceNumber?: string | null;
  date: string;
  dueDate?: string | null;
  balance: number;
  total: number;
  status: string;
};

type PortalBillingResponse = { data?: PortalInvoice[] };

type CheckoutResponse = { data?: { url?: string } };

function isPayable(invoice: PortalInvoice) {
  return Number(invoice.balance) > 0 && invoice.status !== 'PAID' && invoice.status !== 'REFUNDED';
}

export default function BillingPage() {
  const { t } = useTranslation();
  const { fetchJson } = useApiClient();
  const { buildPortalPath, refreshNonce } = usePortalPatientData();
  const [rows, setRows] = useState<PortalInvoice[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [payingId, setPayingId] = useState<string | null>(null);

  const loadBilling = useCallback(async () => {
    try {
      setError(null);
      const response = await fetchJson<PortalBillingResponse>(buildPortalPath('/portal/billing'));
      setRows(Array.isArray(response?.data) ? response.data : []);
    } catch (err: any) {
      setError(err?.message || t('billing.loadError'));
    }
  }, [buildPortalPath, fetchJson, t]);

  useEffect(() => {
    void loadBilling();
  }, [loadBilling, refreshNonce]);

  const handlePay = useCallback(
    async (invoiceId: string) => {
      setError(null);
      setPayingId(invoiceId);
      try {
        const response = await fetchJson<CheckoutResponse>(
          buildPortalPath(`/portal/billing/${invoiceId}/checkout`),
          { method: 'POST' },
        );
        const url = response?.data?.url;
        if (url) {
          window.location.href = url;
        } else {
          setError(t('billing.checkoutMissingUrl'));
        }
      } catch (err: any) {
        setError(err?.message || t('billing.checkoutError'));
      } finally {
        setPayingId(null);
      }
    },
    [buildPortalPath, fetchJson, t],
  );

  return (
    <PortalPage title={t('billing.title')} description={t('billing.description')}>
      {error ? <ErrorBanner message={error} /> : null}
      <TableCard
        headers={[t('billing.invoice'), t('billing.date'), t('billing.dueDate'), t('billing.balance'), t('billing.total'), t('billing.status'), '']}
        rows={(rows || []).map((invoice) => [
          <span key={`${invoice.id}-number`} className="font-medium text-slate-700">
            {invoice.invoiceNumber || invoice.id}
          </span>,
          formatPortalDate(invoice.date),
          formatPortalDate(invoice.dueDate),
          formatPortalCurrency(invoice.balance),
          formatPortalCurrency(invoice.total),
          <span key={`${invoice.id}-status`} className={`pill ${statusPillClass(invoice.status)}`}>
            {invoice.status}
          </span>,
          isPayable(invoice) ? (
            <button
              key={`${invoice.id}-pay`}
              type="button"
              onClick={() => handlePay(invoice.id)}
              disabled={payingId === invoice.id}
              className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-emerald-500 disabled:opacity-60"
            >
              {payingId === invoice.id ? t('billing.starting') : t('billing.payNow')}
            </button>
          ) : (
            <span key={`${invoice.id}-paid`} className="text-sm text-slate-400">
              —
            </span>
          ),
        ])}
        emptyMessage={t('billing.noEntries')}
      />
    </PortalPage>
  );
}
